import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject, BehaviorSubject, of, timer } from 'rxjs';
import { switchMap, map, catchError, delay } from 'rxjs/operators';
import { 
    TestCase, 
    TestStep, 
    TestSuite, 
    TestReport, 
    TestStatus, 
    TestAction, 
    TestConfiguration,
    RouteTestConfig 
} from '../models/test-result.model';

@Injectable({
    providedIn: 'root'
})
export class TestEngineService {
    private currentReport$ = new BehaviorSubject<TestReport | null>(null);
    private testProgress$ = new Subject<{ suite: string; case: string; progress: number }>();
    
    private defaultConfig: TestConfiguration = {
        baseUrl: '',
        timeout: 30000,
        retryCount: 2,
        screenshotOnFailure: true,
        parallelExecution: false,
        routes: [
            { path: '', name: 'Home (Part Upload)', component: 'PartFileUploadComponent', testCases: ['file-upload', 'navigation'] },
            { path: 'upload', name: 'Part File Upload', component: 'PartFileUploadComponent', testCases: ['file-upload', 'navigation'] },
            { path: 'summary', name: 'Quote Summary', component: 'QuoteSummaryComponent', testCases: ['display', 'navigation'] },
            { path: 'assist', name: 'Quote Assist', component: 'QuoteAssistSingleApiComponent', testCases: ['form-validation', 'submit', 'navigation'] },
            { path: 'direct-rfq-list', name: 'Direct RFQ List', component: 'DirectRfqListComponent', testCases: ['list-display', 'filtering', 'navigation'] },
            { path: 'direct-assist', name: 'Direct Quote Assist', component: 'DirectQuoteAssistComponent', testCases: ['form-validation', 'submit', 'navigation'] },
            { path: 'direct-quote-summary', name: 'Direct Quote Summary', component: 'DirectQuoteSummaryComponent', testCases: ['display', 'navigation'] },
            { path: 'rfq-details-drawer', name: 'RFQ Details Drawer', component: 'RfqDetailsDrawerComponent', testCases: ['display', 'navigation'] },
            { path: 'rfq-intake', name: 'RFQ Intake', component: 'RfqIntakeComponent', testCases: ['step-navigation', 'form-validation', 'submit'] }
        ]
    };

    constructor(private router: Router) {}

    getCurrentReport(): Observable<TestReport | null> {
        return this.currentReport$.asObservable();
    }

    getTestProgress(): Observable<{ suite: string; case: string; progress: number }> {
        return this.testProgress$.asObservable();
    }

    async runAllTests(config?: Partial<TestConfiguration>): Promise<TestReport> {
        const testConfig = { ...this.defaultConfig, ...config };
        const report: TestReport = {
            id: this.generateId(),
            name: 'Full UI Test Suite',
            startTime: new Date(),
            testSuites: [],
            totalTests: 0,
            passedTests: 0,
            failedTests: 0,
            successRate: 0,
            environment: 'development'
        };

        this.currentReport$.next(report);

        for (const routeConfig of testConfig.routes) {
            const testSuite = await this.runRouteTests(routeConfig);
            report.testSuites.push(testSuite);
            report.totalTests += testSuite.totalCount;
            report.passedTests += testSuite.successCount;
            report.failedTests += testSuite.failureCount;
        }

        report.endTime = new Date();
        report.duration = report.endTime.getTime() - report.startTime.getTime();
        report.successRate = report.totalTests > 0 ? (report.passedTests / report.totalTests) * 100 : 0;

        this.currentReport$.next(report);
        return report;
    }

    async runSingleRouteTest(routePath: string): Promise<TestSuite> {
        const routeConfig = this.defaultConfig.routes.find(r => r.path === routePath);
        if (!routeConfig) {
            throw new Error(`Route configuration not found for path: ${routePath}`);
        }

        return this.runRouteTests(routeConfig);
    }

    async runSingleRouteTestInWindow(routePath: string, testWindow: Window, rfqParams?: any): Promise<TestSuite> {
        const routeConfig = this.defaultConfig.routes.find(r => r.path === routePath);
        if (!routeConfig) {
            throw new Error(`Route configuration not found for path: ${routePath}`);
        }

        return this.runRouteTestsInWindow(routeConfig, testWindow, rfqParams);
    }

    private async runRouteTests(routeConfig: RouteTestConfig): Promise<TestSuite> {
        const testSuite: TestSuite = {
            id: this.generateId(),
            name: routeConfig.name,
            route: routeConfig.path,
            component: routeConfig.component,
            testCases: [],
            status: TestStatus.RUNNING,
            startTime: new Date(),
            successCount: 0,
            failureCount: 0,
            totalCount: 0
        };

        // Navigate to the route
        try {
            await this.router.navigate([routeConfig.path]);
            await this.wait(2000); // Wait for route to load
        } catch (error) {
            console.error(`Failed to navigate to ${routeConfig.path}:`, error);
        }

        // Generate test cases based on route configuration
        const testCases = this.generateTestCases(routeConfig);
        
        for (const testCase of testCases) {
            testSuite.totalCount++;
            this.testProgress$.next({
                suite: testSuite.name,
                case: testCase.name,
                progress: (testSuite.testCases.length / testCases.length) * 100
            });

            try {
                const result = await this.executeTestCase(testCase);
                if (result.status === TestStatus.PASSED) {
                    testSuite.successCount++;
                } else {
                    testSuite.failureCount++;
                }
                testSuite.testCases.push(result);
            } catch (error) {
                testCase.status = TestStatus.FAILED;
                testCase.error = error instanceof Error ? error.message : String(error);
                testSuite.failureCount++;
                testSuite.testCases.push(testCase);
            }
        }

        testSuite.endTime = new Date();
        testSuite.duration = testSuite.endTime.getTime() - testSuite.startTime!.getTime();
        testSuite.status = testSuite.failureCount === 0 ? TestStatus.PASSED : TestStatus.FAILED;

        return testSuite;
    }

    private async runRouteTestsInWindow(routeConfig: RouteTestConfig, testWindow: Window, rfqParams?: any): Promise<TestSuite> {
        const testSuite: TestSuite = {
            id: this.generateId(),
            name: routeConfig.name,
            route: routeConfig.path,
            component: routeConfig.component,
            testCases: [],
            status: TestStatus.RUNNING,
            startTime: new Date(),
            successCount: 0,
            failureCount: 0,
            totalCount: 0
        };

        console.log(`🧪 Starting tests for ${routeConfig.name} in popup window`);

        // Wait for the window to be fully loaded and stable
        await this.waitForWindowReady(testWindow);

        // Generate test cases based on route configuration
        const testCases = this.generateTestCases(routeConfig);
        
        for (const testCase of testCases) {
            testSuite.totalCount++;
            this.testProgress$.next({
                suite: testSuite.name,
                case: testCase.name,
                progress: (testSuite.testCases.length / testCases.length) * 100
            });

            try {
                console.log(`🔧 Executing test case: ${testCase.name}`);
                const result = await this.executeTestCaseInWindow(testCase, testWindow, rfqParams);
                if (result.status === TestStatus.PASSED) {
                    testSuite.successCount++;
                } else {
                    testSuite.failureCount++;
                }
                testSuite.testCases.push(result);
            } catch (error) {
                console.error(`❌ Test case failed: ${testCase.name}`, error);
                testCase.status = TestStatus.FAILED;
                testCase.error = error instanceof Error ? error.message : String(error);
                testSuite.failureCount++;
                testSuite.testCases.push(testCase);
            }
        }

        testSuite.endTime = new Date();
        testSuite.duration = testSuite.endTime.getTime() - testSuite.startTime!.getTime();
        testSuite.status = testSuite.failureCount === 0 ? TestStatus.PASSED : TestStatus.FAILED;

        console.log(`✅ Test suite completed for ${routeConfig.name}:`, {
            total: testSuite.totalCount,
            passed: testSuite.successCount,
            failed: testSuite.failureCount
        });

        return testSuite;
    }

    private async waitForWindowReady(testWindow: Window): Promise<void> {
        return new Promise((resolve) => {
            const checkReady = () => {
                try {
                    if (testWindow.document.readyState === 'complete') {
                        // Wait additional time for Angular components to initialize
                        setTimeout(resolve, 3000);
                    } else {
                        setTimeout(checkReady, 200);
                    }
                } catch (e) {
                    // Cross-origin issues, just wait
                    setTimeout(resolve, 4000);
                }
            };
            checkReady();
        });
    }

    private async executeTestCaseInWindow(testCase: TestCase, testWindow: Window, rfqParams?: any): Promise<TestCase> {
        const updatedTestCase: TestCase = {
            ...testCase,
            status: TestStatus.RUNNING,
            startTime: new Date()
        };

        try {
            for (let i = 0; i < testCase.steps.length; i++) {
                const step = testCase.steps[i];
                console.log(`🔍 Executing step ${i + 1}/${testCase.steps.length}: ${step.action} - ${step.element || step.value || ''}`);
                
                await this.executeTestStepInWindow(step, testWindow, rfqParams);
                
                if (step.status === TestStatus.FAILED) {
                    throw new Error(step.actualResult || 'Step failed');
                }
            }

            updatedTestCase.status = TestStatus.PASSED;
            updatedTestCase.actualResult = 'All steps completed successfully';
        } catch (error) {
            updatedTestCase.status = TestStatus.FAILED;
            updatedTestCase.error = error instanceof Error ? error.message : String(error);
            updatedTestCase.actualResult = updatedTestCase.error;
        } finally {
            updatedTestCase.endTime = new Date();
            if (updatedTestCase.startTime) {
                updatedTestCase.duration = updatedTestCase.endTime.getTime() - updatedTestCase.startTime.getTime();
            }
        }

        return updatedTestCase;
    }

    private async executeTestStepInWindow(step: TestStep, testWindow: Window, rfqParams?: any): Promise<void> {
        step.status = TestStatus.RUNNING;

        try {
            switch (step.action) {
                case TestAction.ASSERT_ELEMENT_EXISTS:
                    await this.assertElementExistsInWindow(step, testWindow);
                    break;
                case TestAction.ASSERT_ELEMENT_VISIBLE:
                    await this.assertElementVisibleInWindow(step, testWindow);
                    break;
                case TestAction.CLICK:
                    await this.clickElementInWindow(step, testWindow);
                    break;
                case TestAction.INPUT:
                    await this.inputValueInWindow(step, testWindow);
                    break;
                case TestAction.NAVIGATE:
                    await this.navigateInWindow(step, testWindow, rfqParams);
                    break;
                case TestAction.WAIT:
                    await this.wait(parseInt(step.value || '1000', 10));
                    break;
                case TestAction.UPLOAD_FILE:
                    await this.simulateFileUploadInWindow(step, testWindow);
                    break;
                default:
                    step.actualResult = `Action ${step.action} not implemented`;
                    step.status = TestStatus.SKIPPED;
                    return;
            }
            step.status = TestStatus.PASSED;
            step.actualResult = step.expectedResult;
        } catch (error) {
            step.status = TestStatus.FAILED;
            step.actualResult = error instanceof Error ? error.message : String(error);
            throw error;
        }
    }

    private async assertElementExistsInWindow(step: TestStep, testWindow: Window): Promise<void> {
        await this.wait(500);
        const element = this.findAdvancedElementInWindow(step.element!, testWindow);
        if (!element) {
            throw new Error(`Element not found: ${step.element}`);
        }
        console.log(`✅ Found element: ${step.element}`);
    }

    private async assertElementVisibleInWindow(step: TestStep, testWindow: Window): Promise<void> {
        const element = this.findAdvancedElementInWindow(step.element!, testWindow) as HTMLElement;
        if (!element) {
            throw new Error(`Element not found: ${step.element}`);
        }
        
        const rect = element.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) {
            throw new Error(`Element not visible: ${step.element}`);
        }
        console.log(`✅ Element visible: ${step.element}`);
    }

    private async clickElementInWindow(step: TestStep, testWindow: Window): Promise<void> {
        const element = this.findAdvancedElementInWindow(step.element!, testWindow) as HTMLElement;
        if (!element) {
            throw new Error(`Element not found: ${step.element}`);
        }
        
        // Scroll element into view and wait
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        await this.wait(500);
        
        console.log(`🖱️ Clicking element: ${step.element}`, element);
        
        // Try multiple click approaches
        try {
            element.click();
        } catch (e) {
            // Fallback to mouse event using global MouseEvent
            element.dispatchEvent(new MouseEvent('click', { 
                bubbles: true, 
                cancelable: true,
                view: testWindow
            }));
        }
        
        await this.wait(800);
        console.log(`✅ Clicked element: ${step.element}`);
    }

    private async inputValueInWindow(step: TestStep, testWindow: Window): Promise<void> {
        const element = this.findAdvancedElementInWindow(step.element!, testWindow) as HTMLInputElement;
        if (!element) {
            throw new Error(`Input element not found: ${step.element}`);
        }
        
        console.log(`⌨️ Typing into element: ${step.element} -> "${step.value}"`);
        
        // Clear and focus
        element.value = '';
        element.focus();
        await this.wait(200);
        
        // Type value realistically
        await this.typeTextInWindow(element, step.value!, testWindow);
        
        // Trigger all relevant events
        element.dispatchEvent(new Event('input', { bubbles: true }));
        element.dispatchEvent(new Event('change', { bubbles: true }));
        element.dispatchEvent(new Event('blur', { bubbles: true }));
        await this.wait(500);
        
        console.log(`✅ Input completed: ${step.element}`);
    }

    private async simulateFileUploadInWindow(step: TestStep, testWindow: Window): Promise<void> {
        const fileInput = this.findAdvancedElementInWindow(step.element!, testWindow) as HTMLInputElement;
        if (!fileInput) {
            throw new Error(`File input element not found: ${step.element}`);
        }
        
        // Simulate file selection with the demo PDF files
        const fileName = step.value || 'mount-demo.pdf';
        
        console.log(`📁 Simulating file upload: ${fileName}`);
        
        // Create a mock file object
        const mockFile = new File(['mock PDF content'], fileName, { 
            type: 'application/pdf',
            lastModified: Date.now()
        });
        
        // Create a data transfer object and add the file
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(mockFile);
        
        // Set the files property
        fileInput.files = dataTransfer.files;
        
        // Trigger change and input events
        fileInput.dispatchEvent(new Event('change', { bubbles: true }));
        fileInput.dispatchEvent(new Event('input', { bubbles: true }));
        
        await this.wait(1000);
        console.log(`✅ File upload simulated: ${fileName}`);
    }

    private async navigateInWindow(step: TestStep, testWindow: Window, rfqParams?: any): Promise<void> {
        const targetRoute = step.element!; // element field contains the route path
        const baseUrl = testWindow.location.origin;
        
        let fullUrl: string;
        
        // If navigating to assist route and we have RFQ parameters, build URL with query params
        if (targetRoute.includes('/assist') && rfqParams) {
            const queryParams = new URLSearchParams({
                rfqId: rfqParams.rfqId,
                companyId: rfqParams.companyId,
                rfqPartId: rfqParams.rfqPartId,
                contactId: rfqParams.contactId,
                integration: 'test',
                isTrackingEnabled: rfqParams.isTrackingEnabled,
                showHeader: '1'
            });
            fullUrl = `${baseUrl}/assist?${queryParams.toString()}`;
            console.log(`🔧 Quote Assist navigation with RFQ params: ${fullUrl}`);
        } else {
            fullUrl = `${baseUrl}${targetRoute}`;
        }
        
        console.log(`🧭 Navigating popup to: ${targetRoute}`);
        
        // Navigate the popup window to the new route
        testWindow.location.href = fullUrl;
        
        // Wait for navigation to complete
        await this.waitForWindowReady(testWindow);
        
        console.log(`✅ Navigation completed: ${targetRoute}`);
    }

    private findAdvancedElementInWindow(selector: string, testWindow: Window): Element | null {
        const doc = testWindow.document;
        console.log(`🔍 Finding element in popup: ${selector}`);
        
        // Handle multiple selectors (comma-separated)
        if (selector.includes(',')) {
            const selectors = selector.split(',').map(s => s.trim());
            for (const sel of selectors) {
                const element = this.findAdvancedElementInWindow(sel, testWindow);
                if (element) return element;
            }
            return null;
        }

        // Handle custom :contains-text() selector
        if (selector.includes(':contains-text(')) {
            const match = selector.match(/^(.+?):contains-text\("(.+?)"\)(.*)$/);
            if (match) {
                const [, baseSelector, text, suffixSelector] = match;
                const elements = doc.querySelectorAll(baseSelector || '*');
                for (const el of elements) {
                    if (el.textContent?.toLowerCase().includes(text.toLowerCase())) {
                        if (suffixSelector) {
                            const found = el.querySelector(suffixSelector);
                            if (found) {
                                console.log(`✅ Found element with :contains-text() selector: ${selector}`);
                                return found;
                            }
                        } else {
                            console.log(`✅ Found element with :contains-text() selector: ${selector}`);
                            return el;
                        }
                    }
                }
            }
        }

        // Try standard querySelector first
        try {
            let element = doc.querySelector(selector);
            if (element) {
                console.log(`✅ Found element with standard selector: ${selector}`);
                return element;
            }
        } catch (e) {
            console.log(`⚠️ Invalid selector: ${selector}`);
        }

        // Try advanced matching strategies
        
        // Material section row clicking 
        if (selector.includes('.material-row')) {
            const materialRows = doc.querySelectorAll('.material-row');
            for (const row of materialRows) {
                // Check if this is the Material section specifically 
                const h5 = row.querySelector('h5');
                if (h5 && h5.textContent?.includes('Material')) {
                    // Check if it's not disabled
                    const isDisabled = row.classList.contains('disabled-section-overlay');
                    if (!isDisabled) {
                        console.log(`✅ Found enabled Material section: ${selector}`);
                        return row;
                    } else {
                        console.log(`⚠️ Material section is disabled, waiting for data load: ${selector}`);
                    }
                }
                // For generic material-row selector, return first available
                if (selector === '.material-row' || selector.includes('.material-row,')) {
                    console.log(`✅ Found material row: ${selector}`);
                    return row;
                }
            }
        }
        
        // Section header clicking (Material, Tooling, Manufacturing Processes, etc.)
        if (selector.includes('h5:contains-text(')) {
            const match = selector.match(/h5:contains-text\("(.+?)"\)/);
            if (match) {
                const sectionName = match[1];
                const h5Elements = doc.querySelectorAll('h5');
                for (const h5 of h5Elements) {
                    if (h5.textContent?.includes(sectionName)) {
                        // Click on the parent material-row div instead of just the h5
                        const parentRow = h5.closest('.material-row');
                        if (parentRow) {
                            console.log(`✅ Found section header parent: ${selector} -> "${sectionName}"`);
                            return parentRow;
                        }
                        console.log(`✅ Found section header: ${selector} -> "${sectionName}"`);
                        return h5;
                    }
                }
            }
        }

        // Collapseable container detection
        if (selector.includes('collapseable')) {
            // Try different variations of collapseable containers
            const containers = doc.querySelectorAll('.collapseable-container, [class*="collapseable"], .collapse, .collapse.show');
            if (containers.length > 0) {
                console.log(`✅ Found collapseable container: ${selector}`);
                return containers[0];
            }
            
            // Check if any material-row is expanded (has visible content)
            const materialRows = doc.querySelectorAll('.material-row');
            for (const row of materialRows) {
                const h5 = row.querySelector('h5');
                if (h5 && h5.textContent?.includes('Material')) {
                    // Check if the row has visible form content
                    const inputs = row.querySelectorAll('input');
                    if (inputs.length > 0) {
                        console.log(`✅ Found expanded material section (has inputs): ${selector}`);
                        return row;
                    }
                }
            }
        }

        // Material section specific inputs
        if (selector.includes('placeholder="Material Name"')) {
            const element = doc.querySelector('input[placeholder="Material Name"]');
            if (element) {
                console.log(`✅ Found material name input: ${selector}`);
                return element;
            }
        }

        if (selector.includes('placeholder="0.00"')) {
            const costInputs = doc.querySelectorAll('input[placeholder="0.00"]:not([disabled])');
            for (const input of costInputs) {
                const parent = input.closest('.input-group, .editable-textbox');
                if (parent && parent.innerHTML.includes('$')) {
                    console.log(`✅ Found cost input: ${selector}`);
                    return input;
                }
            }
            // Fallback to any cost input
            if (costInputs.length > 0) {
                console.log(`✅ Found fallback cost input: ${selector}`);
                return costInputs[0];
            }
        }

        // Manufacturing process elements
        if (selector.includes('cycleTimePerUnit') || selector.includes('totalLaborTimeInMinutes')) {
            const inputs = doc.querySelectorAll('input[type="text"]:not([disabled])');
            for (const input of inputs) {
                const parent = input.closest('.row, .col-md-3, .manufacturing-row');
                if (parent) {
                    const label = parent.querySelector('label');
                    if (label && (label.textContent?.includes('Cycle Time') || 
                                  label.textContent?.includes('Setup Time') ||
                                  label.textContent?.includes('Machine Time'))) {
                        console.log(`✅ Found manufacturing input: ${selector}`);
                        return input;
                    }
                }
            }
        }

        // Button matching
        if (selector.includes('button') || selector.includes('.light-dark-blue')) {
            const buttons = doc.querySelectorAll('button');
            for (const btn of buttons) {
                const btnText = btn.textContent?.toLowerCase() || '';
                if (btnText.includes('continue') || 
                    btnText.includes('proceed') ||
                    btnText.includes('add to quote') ||
                    btn.className.includes('light-dark-blue') ||
                    btn.className.includes('blue')) {
                    console.log(`✅ Found button: ${selector} -> "${btn.textContent}"`);
                    return btn;
                }
            }
        }

        // Generic input matching
        if (selector.includes('input[type="text"]:not([disabled])')) {
            const inputs = doc.querySelectorAll('input[type="text"]:not([disabled])');
            if (inputs.length > 0) {
                console.log(`✅ Found first available input: ${selector}`);
                return inputs[0];
            }
        }

        // Debug: Show available elements
        console.log(`❌ Could not find element in popup: ${selector}`);
        console.log(`📋 Available elements in popup:`, doc.querySelectorAll('*').length);
        console.log(`📋 Available material sections:`, doc.querySelectorAll('[class*="material"]').length);
        console.log(`📋 Available buttons:`, doc.querySelectorAll('button').length);
        console.log(`📋 Available inputs:`, doc.querySelectorAll('input').length);
        console.log(`📋 Available enabled inputs:`, doc.querySelectorAll('input:not([disabled])').length);
        
        // Log some button texts for debugging
        const buttons = doc.querySelectorAll('button');
        console.log(`📋 Button texts:`, Array.from(buttons).slice(0, 5).map(b => b.textContent?.trim()));
        
        return null;
    }

    private async typeTextInWindow(element: HTMLInputElement, text: string, testWindow: Window): Promise<void> {
        for (let i = 0; i < text.length; i++) {
            element.value += text.charAt(i);
            element.dispatchEvent(new Event('input', { bubbles: true }));
            await this.wait(50); // Realistic typing speed
        }
    }

    private generateTestCases(routeConfig: RouteTestConfig): TestCase[] {
        const testCases: TestCase[] = [];

        // Basic navigation test
        testCases.push({
            id: this.generateId(),
            name: 'Page Load Test',
            description: `Verify ${routeConfig.name} page loads correctly`,
            steps: [
                {
                    id: this.generateId(),
                    action: TestAction.ASSERT_ELEMENT_EXISTS,
                    element: 'body',
                    expectedResult: 'Page body should exist',
                    status: TestStatus.PENDING
                }
            ],
            expectedResult: 'Page should load without errors',
            status: TestStatus.PENDING
        });

        // Component-specific tests based on route
        switch (routeConfig.component) {
            case 'PartFileUploadComponent':
                testCases.push(...this.generateFileUploadTests());
                break;
            case 'QuoteAssistSingleApiComponent':
            case 'DirectQuoteAssistComponent':
                testCases.push(...this.generateQuoteAssistTests());
                break;
            case 'QuoteSummaryComponent':
            case 'DirectQuoteSummaryComponent':
                testCases.push(...this.generateSummaryTests());
                break;
            case 'RfqIntakeComponent':
                testCases.push(...this.generateRfqIntakeTests());
                break;
            case 'DirectRfqListComponent':
                testCases.push(...this.generateListTests());
                break;
            default:
                testCases.push(...this.generateGenericTests());
                break;
        }

        return testCases;
    }

    private generateFileUploadTests(): TestCase[] {
        return [
            {
                id: this.generateId(),
                name: 'File Input Exists',
                description: 'Verify file input element exists',
                steps: [
                    {
                        id: this.generateId(),
                        action: TestAction.ASSERT_ELEMENT_EXISTS,
                        element: 'input[type="file"]',
                        expectedResult: 'File input should exist',
                        status: TestStatus.PENDING
                    }
                ],
                expectedResult: 'File input element should be present',
                status: TestStatus.PENDING
            },
            {
                id: this.generateId(),
                name: 'Upload Button Interaction',
                description: 'Test file upload button functionality',
                steps: [
                    {
                        id: this.generateId(),
                        action: TestAction.CLICK,
                        element: 'input[type="file"]',
                        expectedResult: 'File dialog should open',
                        status: TestStatus.PENDING
                    }
                ],
                expectedResult: 'Upload functionality should be accessible',
                status: TestStatus.PENDING
            },
            {
                id: this.generateId(),
                name: 'Mount Demo PDF Upload',
                description: 'Test uploading mount-demo.pdf from testing files',
                steps: [
                    {
                        id: this.generateId(),
                        action: TestAction.UPLOAD_FILE,
                        element: 'input[type="file"]',
                        value: 'mount-demo.pdf',
                        expectedResult: 'File should be selected and upload should trigger',
                        status: TestStatus.PENDING
                    },
                    {
                        id: this.generateId(),
                        action: TestAction.WAIT,
                        value: 2000,
                        expectedResult: 'Wait for upload processing',
                        status: TestStatus.PENDING
                    }
                ],
                expectedResult: 'Mount demo PDF should upload successfully',
                status: TestStatus.PENDING
            },
            {
                id: this.generateId(),
                name: 'PDF File Upload Simulation',
                description: 'Simulate uploading demo PDF files (mount-demo.pdf, knife-foot-demo.pdf, LiquidMixer-demo.pdf)',
                steps: [
                    {
                        id: this.generateId(),
                        action: TestAction.ASSERT_ELEMENT_EXISTS,
                        element: 'input[type="file"]',
                        expectedResult: 'File input should accept PDF files',
                        status: TestStatus.PENDING
                    },
                    {
                        id: this.generateId(),
                        action: TestAction.ASSERT_ELEMENT_EXISTS,
                        element: '.upload-area, .file-drop-zone, .upload-container',
                        expectedResult: 'Upload area should be visible',
                        status: TestStatus.PENDING
                    }
                ],
                expectedResult: 'Should be ready to accept PDF file uploads from testing/files directory',
                status: TestStatus.PENDING
            },
            {
                id: this.generateId(),
                name: 'File Type Validation',
                description: 'Verify only valid file types are accepted',
                steps: [
                    {
                        id: this.generateId(),
                        action: TestAction.ASSERT_ELEMENT_EXISTS,
                        element: 'input[type="file"][accept]',
                        expectedResult: 'File input should have accept attribute for validation',
                        status: TestStatus.PENDING
                    }
                ],
                expectedResult: 'File type validation should be in place',
                status: TestStatus.PENDING
            }
        ];
    }

    private generateQuoteAssistTests(): TestCase[] {
        return [
            {
                id: 'page-load',
                name: 'Quote Assist Page Load & Data Verification',
                description: 'Verify Quote Assist page loads with RFQ data and all essential elements',
                steps: [
                    { 
                        id: this.generateId(),
                        action: TestAction.WAIT, 
                        value: 5000, 
                        expectedResult: 'Wait for Quote Assist page and RFQ data to load',
                        status: TestStatus.PENDING
                    },
                    { 
                        id: this.generateId(),
                        action: TestAction.ASSERT_ELEMENT_EXISTS, 
                        element: '.quote-assist-sec', 
                        expectedResult: 'Quote assist section should exist',
                        status: TestStatus.PENDING
                    },
                    { 
                        id: this.generateId(),
                        action: TestAction.ASSERT_ELEMENT_EXISTS, 
                        element: '.material-row', 
                        expectedResult: 'Material sections should exist',
                        status: TestStatus.PENDING
                    },
                    { 
                        id: this.generateId(),
                        action: TestAction.ASSERT_ELEMENT_EXISTS, 
                        element: '.part-name, .file-name, .rfq-name, .top-part-name, h4:contains-text("Quoting")', 
                        expectedResult: 'RFQ information should be displayed',
                        status: TestStatus.PENDING
                    }
                ],
                expectedResult: 'Quote Assist page should load with RFQ data and all sections',
                status: TestStatus.PENDING
            },
            {
                id: 'material-form',
                name: 'Material Section Form Filling',
                description: 'Fill out material information with realistic data', 
                steps: [
                    { 
                        id: this.generateId(),
                        action: TestAction.WAIT, 
                        value: 3000, 
                        expectedResult: 'Wait for data to fully load',
                        status: TestStatus.PENDING
                    },
                    { 
                        id: this.generateId(),
                        action: TestAction.ASSERT_ELEMENT_EXISTS, 
                        element: '.material-row:not(.disabled-section-overlay), .material-row', 
                        expectedResult: 'Material section should be available and enabled',
                        status: TestStatus.PENDING
                    },
                    { 
                        id: this.generateId(),
                        action: TestAction.CLICK, 
                        element: '.material-row, .material-row .heading-sec, .material-row h5', 
                        expectedResult: 'Click on Material section to expand',
                        status: TestStatus.PENDING
                    },
                    { 
                        id: this.generateId(),
                        action: TestAction.WAIT, 
                        value: 3000, 
                        expectedResult: 'Wait for material section to expand',
                        status: TestStatus.PENDING
                    },
                    { 
                        id: this.generateId(),
                        action: TestAction.ASSERT_ELEMENT_EXISTS, 
                        element: '.collapseable-container, .material-row[style*="display: block"], .material-row .collapseable-container, [class*="collapseable"]', 
                        expectedResult: 'Material section should be expanded',
                        status: TestStatus.PENDING
                    },
                    { 
                        id: this.generateId(),
                        action: TestAction.INPUT, 
                        element: 'input[placeholder="Material Name"], .material-row input[placeholder="Material Name"], .collapseable-container input[placeholder="Material Name"], input[ngModel*="materialItem.name"], input[ngModel*="name"]', 
                        value: 'Aluminum 6061-T6', 
                        expectedResult: 'Material name should be entered',
                        status: TestStatus.PENDING
                    },
                    { 
                        id: this.generateId(),
                        action: TestAction.INPUT, 
                        element: 'input[placeholder="0.00"]:not([disabled]), .material-row input[placeholder="0.00"]:not([disabled]), .collapseable-container input[placeholder="0.00"]:not([disabled]), input[ngModel*="totalCost"]', 
                        value: '125.50', 
                        expectedResult: 'Material cost should be entered',
                        status: TestStatus.PENDING
                    },
                    { 
                        id: this.generateId(),
                        action: TestAction.WAIT, 
                        value: 2000, 
                        expectedResult: 'Wait for input processing',
                        status: TestStatus.PENDING
                    },
                    { 
                        id: this.generateId(),
                        action: TestAction.CLICK, 
                        element: 'button:contains-text("Add To Quote"), .material-row button:contains-text("Add To Quote"), button.light-dark-blue', 
                        expectedResult: 'Add To Quote button should be clicked',
                        status: TestStatus.PENDING
                    },
                    { 
                        id: this.generateId(),
                        action: TestAction.WAIT, 
                        value: 3000, 
                        expectedResult: 'Wait for next section to be enabled',
                        status: TestStatus.PENDING
                    }
                ],
                expectedResult: 'Material section should be filled and submitted successfully',
                status: TestStatus.PENDING
            },
            {
                id: 'tooling-section',
                name: 'Tooling Section Configuration', 
                description: 'Configure tooling expenses',
                steps: [
                    { 
                        id: this.generateId(),
                        action: TestAction.CLICK, 
                        element: '.material-row h5:contains-text("Tooling")', 
                        expectedResult: 'Click on Tooling section to expand',
                        status: TestStatus.PENDING
                    },
                    { 
                        id: this.generateId(),
                        action: TestAction.WAIT, 
                        value: 2000, 
                        expectedResult: 'Wait for tooling section to expand',
                        status: TestStatus.PENDING
                    },
                    { 
                        id: this.generateId(),
                        action: TestAction.CLICK, 
                        element: 'button:contains-text("Add To Quote")', 
                        expectedResult: 'Add tooling to quote',
                        status: TestStatus.PENDING
                    },
                    { 
                        id: this.generateId(),
                        action: TestAction.WAIT, 
                        value: 2000, 
                        expectedResult: 'Wait for next section to be enabled',
                        status: TestStatus.PENDING
                    }
                ],
                expectedResult: 'Tooling section should be configured',
                status: TestStatus.PENDING
            },
            {
                id: 'manufacturing-process',
                name: 'Manufacturing Process Configuration',
                description: 'Configure manufacturing processes with cycle times',
                steps: [
                    { 
                        id: this.generateId(),
                        action: TestAction.CLICK, 
                        element: '.material-row h5:contains-text("Manufacturing Processes")', 
                        expectedResult: 'Click on Manufacturing Processes section to expand',
                        status: TestStatus.PENDING
                    },
                    { 
                        id: this.generateId(),
                        action: TestAction.WAIT, 
                        value: 2000, 
                        expectedResult: 'Wait for manufacturing section to expand',
                        status: TestStatus.PENDING
                    },
                    { 
                        id: this.generateId(),
                        action: TestAction.INPUT, 
                        element: 'input[placeholder="0"]:not([disabled]):not(.disabled-input)', 
                        value: '15', 
                        expectedResult: 'Cycle time should be entered',
                        status: TestStatus.PENDING
                    },
                    { 
                        id: this.generateId(),
                        action: TestAction.CLICK, 
                        element: 'button:contains-text("Continue")', 
                        expectedResult: 'Continue to next section',
                        status: TestStatus.PENDING
                    }
                ],
                expectedResult: 'Manufacturing processes should be configured',
                status: TestStatus.PENDING
            },
            {
                id: 'workflow-progression',
                name: 'Workflow Progression Test',
                description: 'Navigate through remaining sections to complete quote',
                steps: [
                    { 
                        id: this.generateId(),
                        action: TestAction.WAIT, 
                        value: 2000, 
                        expectedResult: 'Wait before proceeding',
                        status: TestStatus.PENDING
                    },
                    { 
                        id: this.generateId(),
                        action: TestAction.CLICK, 
                        element: 'button:contains-text("Add To Quote"), button.light-dark-blue', 
                        expectedResult: 'Continue through workflow',
                        status: TestStatus.PENDING
                    },
                    { 
                        id: this.generateId(),
                        action: TestAction.WAIT, 
                        value: 2000, 
                        expectedResult: 'Wait for next section',
                        status: TestStatus.PENDING
                    },
                    { 
                        id: this.generateId(),
                        action: TestAction.CLICK, 
                        element: 'button:contains-text("Add To Quote"), button.light-dark-blue', 
                        expectedResult: 'Continue through workflow',
                        status: TestStatus.PENDING
                    },
                    { 
                        id: this.generateId(),
                        action: TestAction.WAIT, 
                        value: 2000, 
                        expectedResult: 'Wait for next section',
                        status: TestStatus.PENDING
                    },
                    { 
                        id: this.generateId(),
                        action: TestAction.CLICK, 
                        element: 'button:contains-text("Add To Quote"), button.light-dark-blue', 
                        expectedResult: 'Continue through workflow',
                        status: TestStatus.PENDING
                    },
                    { 
                        id: this.generateId(),
                        action: TestAction.WAIT, 
                        value: 3000, 
                        expectedResult: 'Wait for final section',
                        status: TestStatus.PENDING
                    },
                    { 
                        id: this.generateId(),
                        action: TestAction.CLICK, 
                        element: 'button:contains-text("Proceed To Quote Summary"), button:contains-text("Continue"), button.light-dark-blue', 
                        expectedResult: 'Complete the quote workflow',
                        status: TestStatus.PENDING
                    }
                ],
                expectedResult: 'Should navigate through all sections and complete the quote',
                status: TestStatus.PENDING
            },
            {
                id: 'quote-validation',
                name: 'Quote Validation & Results',
                description: 'Verify quote was processed and results are displayed',
                steps: [
                    { 
                        id: this.generateId(),
                        action: TestAction.ASSERT_ELEMENT_EXISTS, 
                        element: '.summary-sec, .quote-summary, .total-cost, .footer-right-sec', 
                        expectedResult: 'Quote summary or results should be visible',
                        status: TestStatus.PENDING
                    }
                ],
                expectedResult: 'Quote processing should complete with visible results',
                status: TestStatus.PENDING
            }
        ];
    }

    private generateSummaryTests(): TestCase[] {
        return [
            {
                id: this.generateId(),
                name: 'Summary Display',
                description: 'Verify summary information is displayed',
                steps: [
                    {
                        id: this.generateId(),
                        action: TestAction.ASSERT_ELEMENT_EXISTS,
                        element: '.summary-container, .quote-summary, [class*="summary"]',
                        expectedResult: 'Summary container should exist',
                        status: TestStatus.PENDING
                    }
                ],
                expectedResult: 'Summary page should display content',
                status: TestStatus.PENDING
            }
        ];
    }

    private generateRfqIntakeTests(): TestCase[] {
        return [
            {
                id: this.generateId(),
                name: 'Step Navigation',
                description: 'Test multi-step form navigation',
                steps: [
                    {
                        id: this.generateId(),
                        action: TestAction.ASSERT_ELEMENT_EXISTS,
                        element: '.step, .stepper, [class*="step"]',
                        expectedResult: 'Step indicators should exist',
                        status: TestStatus.PENDING
                    }
                ],
                expectedResult: 'RFQ intake should show step navigation',
                status: TestStatus.PENDING
            }
        ];
    }

    private generateListTests(): TestCase[] {
        return [
            {
                id: this.generateId(),
                name: 'List Container',
                description: 'Verify list container exists',
                steps: [
                    {
                        id: this.generateId(),
                        action: TestAction.ASSERT_ELEMENT_EXISTS,
                        element: '.list-container, .rfq-list, table, [class*="list"]',
                        expectedResult: 'List container should exist',
                        status: TestStatus.PENDING
                    }
                ],
                expectedResult: 'List page should display container',
                status: TestStatus.PENDING
            }
        ];
    }

    private generateGenericTests(): TestCase[] {
        return [
            {
                id: this.generateId(),
                name: 'Component Rendered',
                description: 'Verify component is rendered',
                steps: [
                    {
                        id: this.generateId(),
                        action: TestAction.ASSERT_ELEMENT_EXISTS,
                        element: '.component, .container, .content',
                        expectedResult: 'Component content should exist',
                        status: TestStatus.PENDING
                    }
                ],
                expectedResult: 'Component should render successfully',
                status: TestStatus.PENDING
            }
        ];
    }

    private async executeTestCase(testCase: TestCase): Promise<TestCase> {
        testCase.status = TestStatus.RUNNING;
        const startTime = Date.now();

        for (const step of testCase.steps) {
            try {
                await this.executeTestStep(step);
                if (step.status === TestStatus.FAILED) {
                    testCase.status = TestStatus.FAILED;
                    testCase.error = step.actualResult || 'Step failed';
                    break;
                }
            } catch (error) {
                step.status = TestStatus.FAILED;
                step.actualResult = error instanceof Error ? error.message : String(error);
                testCase.status = TestStatus.FAILED;
                testCase.error = step.actualResult;
                break;
            }
        }

        if (testCase.status === TestStatus.RUNNING) {
            testCase.status = TestStatus.PASSED;
            testCase.actualResult = 'All steps completed successfully';
        }

        testCase.duration = Date.now() - startTime;
        return testCase;
    }

    private async executeTestStep(step: TestStep): Promise<void> {
        step.status = TestStatus.RUNNING;

        try {
            switch (step.action) {
                case TestAction.ASSERT_ELEMENT_EXISTS:
                    await this.assertElementExists(step);
                    break;
                case TestAction.ASSERT_ELEMENT_VISIBLE:
                    await this.assertElementVisible(step);
                    break;
                case TestAction.CLICK:
                    await this.clickElement(step);
                    break;
                case TestAction.INPUT:
                    await this.inputValue(step);
                    break;
                case TestAction.WAIT:
                    await this.wait(step.value || 1000);
                    break;
                case TestAction.UPLOAD_FILE:
                    await this.simulateFileUpload(step);
                    break;
                default:
                    step.actualResult = `Action ${step.action} not implemented`;
                    step.status = TestStatus.SKIPPED;
                    return;
            }
            step.status = TestStatus.PASSED;
            step.actualResult = step.expectedResult;
        } catch (error) {
            step.status = TestStatus.FAILED;
            step.actualResult = error instanceof Error ? error.message : String(error);
            throw error;
        }
    }

    private async assertElementExists(step: TestStep): Promise<void> {
        await this.wait(500); // Wait for potential DOM updates
        const element = this.findAdvancedElement(step.element!);
        if (!element) {
            throw new Error(`Element not found: ${step.element}`);
        }
    }

    private async assertElementVisible(step: TestStep): Promise<void> {
        const element = this.findAdvancedElement(step.element!) as HTMLElement;
        if (!element) {
            throw new Error(`Element not found: ${step.element}`);
        }
        
        const rect = element.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) {
            throw new Error(`Element not visible: ${step.element}`);
        }
    }

    private async clickElement(step: TestStep): Promise<void> {
        const element = this.findAdvancedElement(step.element!) as HTMLElement;
        if (!element) {
            throw new Error(`Element not found: ${step.element}`);
        }
        
        // Scroll element into view and wait
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        await this.wait(500);
        
        // Try multiple click approaches
        try {
            element.click();
        } catch (e) {
            // Fallback to mouse event
            element.dispatchEvent(new MouseEvent('click', { 
                bubbles: true, 
                cancelable: true,
                view: window
            }));
        }
        
        await this.wait(800); // Wait for click effects and potential navigation
    }

    private async inputValue(step: TestStep): Promise<void> {
        const element = this.findAdvancedElement(step.element!) as HTMLInputElement;
        if (!element) {
            throw new Error(`Input element not found: ${step.element}`);
        }
        
        // Clear and focus
        element.value = '';
        element.focus();
        await this.wait(200);
        
        // Type value realistically
        await this.typeText(element, step.value!);
        
        // Trigger all relevant events
        element.dispatchEvent(new Event('input', { bubbles: true }));
        element.dispatchEvent(new Event('change', { bubbles: true }));
        element.dispatchEvent(new Event('blur', { bubbles: true }));
        await this.wait(500);
    }

    private findAdvancedElement(selector: string): Element | null {
        console.log(`🔍 Finding element: ${selector}`);
        
        // Try standard querySelector first
        let element = document.querySelector(selector);
        if (element) {
            console.log(`✅ Found element with standard selector: ${selector}`);
            return element;
        }

        // Handle pseudo-selectors like :contains()
        if (selector.includes(':contains(')) {
            const match = selector.match(/^(.+?):contains\("(.+?)"\)(.*)$/);
            if (match) {
                const [, baseSelector, text, suffixSelector] = match;
                const elements = document.querySelectorAll(baseSelector || '*');
                for (const el of elements) {
                    if (el.textContent?.includes(text)) {
                        if (suffixSelector) {
                            const found = el.querySelector(suffixSelector);
                            if (found) {
                                console.log(`✅ Found element with :contains() selector: ${selector}`);
                                return found;
                            }
                        } else {
                            console.log(`✅ Found element with :contains() selector: ${selector}`);
                            return el;
                        }
                    }
                }
            }
        }

        // Handle nth-child selectors with fallbacks
        if (selector.includes(':nth-child(')) {
            const parts = selector.split(':nth-child(');
            if (parts.length === 2) {
                const baseSelector = parts[0];
                const elements = document.querySelectorAll(baseSelector);
                if (elements.length > 0) {
                    console.log(`✅ Found element with nth-child fallback: ${selector}`);
                    return elements[0]; // fallback to first element
                }
            }
        }

        // Try fuzzy matching for common patterns
        if (selector.includes('.material-row')) {
            element = document.querySelector('.material-row') || 
                      document.querySelector('[class*="material"]') ||
                      document.querySelector('.material-section');
            if (element) {
                console.log(`✅ Found element with .material-row fuzzy match: ${selector}`);
                return element;
            }
        }

        if (selector.includes('.light-dark-blue')) {
            // Look for buttons with continue text
            const buttons = document.querySelectorAll('button');
            for (const btn of buttons) {
                if (btn.textContent?.toLowerCase().includes('continue') || 
                    btn.textContent?.toLowerCase().includes('proceed') ||
                    btn.textContent?.toLowerCase().includes('add to quote') ||
                    btn.className.includes('blue')) {
                    console.log(`✅ Found button with continue/proceed text: ${selector}`);
                    return btn;
                }
            }
            
            element = document.querySelector('.light-dark-blue') || 
                      document.querySelector('button[class*="blue"]') ||
                      document.querySelector('button[class*="primary"]');
            if (element) {
                console.log(`✅ Found element with .light-dark-blue fuzzy match: ${selector}`);
                return element;
            }
        }

        // Try attribute-based fallbacks for inputs
        if (selector.includes('placeholder=')) {
            const placeholderMatch = selector.match(/placeholder="([^"]+)"/);
            if (placeholderMatch) {
                element = document.querySelector(`[placeholder="${placeholderMatch[1]}"]`) ||
                          document.querySelector(`[placeholder*="${placeholderMatch[1].substring(0, 5)}"]`);
                if (element) {
                    console.log(`✅ Found element with placeholder match: ${selector}`);
                    return element;
                }
            }
        }

        // Try input type fallbacks
        if (selector.includes('input[placeholder="Material Name"]')) {
            element = document.querySelector('input[placeholder*="Material"]') ||
                      document.querySelector('input[placeholder*="Name"]') ||
                      document.querySelector('input[ng-reflect-model*="name"]');
            if (element) {
                console.log(`✅ Found material name input with fallback: ${selector}`);
                return element;
            }
        }

        if (selector.includes('input[placeholder="0.00"]')) {
            const costInputs = document.querySelectorAll('input[placeholder="0.00"], input[placeholder*="0"], input[type="text"]');
            for (const input of costInputs) {
                const parent = input.closest('.col-md-4, .input-group, .editable-textbox');
                if (parent && parent.innerHTML.includes('$')) {
                    console.log(`✅ Found cost input with $ symbol: ${selector}`);
                    return input;
                }
            }
        }

        // Debug: Show available elements
        console.log(`❌ Could not find element: ${selector}`);
        console.log(`📋 Available elements:`, document.querySelectorAll('*').length);
        console.log(`📋 Available material-rows:`, document.querySelectorAll('[class*="material"]').length);
        console.log(`📋 Available buttons:`, document.querySelectorAll('button').length);
        console.log(`📋 Available inputs:`, document.querySelectorAll('input').length);
        
        return null;
    }

    private async typeText(element: HTMLInputElement, text: string): Promise<void> {
        for (let i = 0; i < text.length; i++) {
            element.value += text.charAt(i);
            element.dispatchEvent(new Event('input', { bubbles: true }));
            await this.wait(50); // Realistic typing speed
        }
    }

    private async simulateFileUpload(step: TestStep): Promise<void> {
        const fileInput = this.findAdvancedElement(step.element!) as HTMLInputElement;
        if (!fileInput) {
            throw new Error(`File input element not found: ${step.element}`);
        }
        
        // Simulate file selection with the demo PDF files
        const fileName = step.value || 'mount-demo.pdf';
        
        // Create a mock file object (for testing purposes)
        const mockFile = new File(['mock PDF content'], fileName, { 
            type: 'application/pdf',
            lastModified: Date.now()
        });
        
        // Create a data transfer object and add the file
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(mockFile);
        
        // Set the files property
        fileInput.files = dataTransfer.files;
        
        // Trigger change and input events
        fileInput.dispatchEvent(new Event('change', { bubbles: true }));
        fileInput.dispatchEvent(new Event('input', { bubbles: true }));
        
        await this.wait(1000); // Wait for upload processing
    }

    private wait(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    private generateId(): string {
        return Date.now().toString(36) + Math.random().toString(36).substr(2);
    }

    generateHtmlReport(report: TestReport): string {
        return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>UI Test Report - ${report.name}</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 20px; background-color: #f5f5f5; }
        .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 20px; border-radius: 8px; margin-bottom: 20px; }
        .summary { display: flex; gap: 20px; margin-bottom: 20px; }
        .summary-card { background: white; padding: 15px; border-radius: 8px; flex: 1; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
        .suite { background: white; margin: 10px 0; padding: 15px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
        .suite-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
        .status { padding: 5px 10px; border-radius: 20px; color: white; font-size: 12px; }
        .passed { background-color: #28a745; }
        .failed { background-color: #dc3545; }
        .pending { background-color: #ffc107; color: black; }
        .running { background-color: #17a2b8; }
        .test-case { margin: 10px 0; padding: 10px; border-left: 4px solid #e9ecef; background: #f8f9fa; }
        .test-case.passed { border-left-color: #28a745; }
        .test-case.failed { border-left-color: #dc3545; }
        .steps { margin-top: 10px; }
        .step { margin: 5px 0; padding: 5px; font-size: 14px; }
        .error { background: #f8d7da; color: #721c24; padding: 10px; border-radius: 4px; margin: 5px 0; }
        .progress-bar { width: 100%; height: 20px; background: #e9ecef; border-radius: 10px; overflow: hidden; }
        .progress-fill { height: 100%; background: #28a745; transition: width 0.3s; }
        .metric { text-align: center; }
        .metric h3 { margin: 0; font-size: 24px; }
        .metric p { margin: 5px 0 0 0; color: #666; }
    </style>
</head>
<body>
    <div class="header">
        <h1>🧪 UI Test Report</h1>
        <p><strong>${report.name}</strong></p>
        <p>Generated: ${report.startTime.toLocaleString()}</p>
        ${report.endTime ? `<p>Duration: ${report.duration ? Math.round(report.duration / 1000) : 0}s</p>` : ''}
    </div>

    <div class="summary">
        <div class="summary-card metric">
            <h3>${report.totalTests}</h3>
            <p>Total Tests</p>
        </div>
        <div class="summary-card metric">
            <h3 style="color: #28a745">${report.passedTests}</h3>
            <p>Passed</p>
        </div>
        <div class="summary-card metric">
            <h3 style="color: #dc3545">${report.failedTests}</h3>
            <p>Failed</p>
        </div>
        <div class="summary-card metric">
            <h3 style="color: #17a2b8">${Math.round(report.successRate)}%</h3>
            <p>Success Rate</p>
        </div>
    </div>

    <div class="progress-bar">
        <div class="progress-fill" style="width: ${report.successRate}%"></div>
    </div>

    ${report.testSuites.map(suite => `
        <div class="suite">
            <div class="suite-header">
                <h2>📄 ${suite.name}</h2>
                <span class="status ${suite.status}">${suite.status.toUpperCase()}</span>
            </div>
            <p><strong>Route:</strong> /${suite.route}</p>
            <p><strong>Component:</strong> ${suite.component}</p>
            <p><strong>Tests:</strong> ${suite.totalCount} (${suite.successCount} passed, ${suite.failureCount} failed)</p>
            ${suite.duration ? `<p><strong>Duration:</strong> ${Math.round(suite.duration / 1000)}s</p>` : ''}
            
            <div class="test-cases">
                ${suite.testCases.map(testCase => `
                    <div class="test-case ${testCase.status}">
                        <h4>🧪 ${testCase.name} <span class="status ${testCase.status}">${testCase.status.toUpperCase()}</span></h4>
                        <p>${testCase.description}</p>
                        ${testCase.duration ? `<p><small>Duration: ${testCase.duration}ms</small></p>` : ''}
                        
                        ${testCase.error ? `<div class="error"><strong>Error:</strong> ${testCase.error}</div>` : ''}
                        
                        <div class="steps">
                            <strong>Steps:</strong>
                            ${testCase.steps.map(step => `
                                <div class="step">
                                    <span class="status ${step.status}">${step.action}</span>
                                    ${step.element ? `<code>${step.element}</code>` : ''}
                                    ${step.value ? `<em>Value: ${step.value}</em>` : ''}
                                    <br><small>${step.expectedResult || 'No expected result'}</small>
                                    ${step.actualResult && step.actualResult !== step.expectedResult ? 
                                        `<br><small style="color: #dc3545;">Actual: ${step.actualResult}</small>` : ''}
                                </div>
                            `).join('')}
                        </div>
                    </div>
                `).join('')}
            </div>
        </div>
    `).join('')}

    <div style="text-align: center; margin-top: 40px; padding: 20px; color: #666;">
        <p>Report generated by Otto Quote Assist UI Test Framework</p>
        <p>Environment: ${report.environment}</p>
    </div>
</body>
</html>`;
    }
} 